'use client';

import React, { useState, useEffect } from 'react';
import { Download, Trash2, Copy, CheckCircle, ExternalLink, Loader2, ImagePlus } from 'lucide-react';
import { toast } from 'sonner';
import { useVisualLab } from '@/context/VisualLabContext';
import type { VisualLabImage } from '@/context/VisualLabContext';

interface GeneratedImage {
  id: string;
  prompt: string;
  style: string;
  ratio: string;
  platform: string;
  color: string;
  seed?: number;
  created: string;
  status: 'bereit' | 'generiert' | 'fehlgeschlagen';
  b64_json?: string;
}

interface VisualLabGalleryProps {
  generating?: boolean;
  newImages?: GeneratedImage[];
}

const gradients = [
  'from-orange-950 to-black',
  'from-blue-950 to-black',
  'from-gray-900 to-black',
  'from-zinc-900 to-black',
  'from-orange-900 to-zinc-950',
  'from-slate-900 to-black',
];

/* ── Skeleton card for images being generated ── */
function ImageSkeleton() {
  return (
    <div className="punk-card overflow-hidden animate-pulse">
      <div
        className="bg-surface-2 relative flex items-center justify-center"
        style={{ aspectRatio: '1', maxHeight: '200px' }}
      >
        <div className="flex flex-col items-center gap-2">
          <Loader2 size={24} className="text-accent animate-spin" />
          <span className="text-xs mono text-muted">Generiert…</span>
        </div>
      </div>
      <div className="p-3 space-y-2">
        <div className="h-3 w-3/4 rounded bg-surface-2" />
        <div className="h-3 w-1/2 rounded bg-surface-2" />
        <div className="h-3 w-1/3 rounded bg-surface-2" />
      </div>
    </div>
  );
}

export default function VisualLabGallery({ generating = false, newImages }: VisualLabGalleryProps) {
  const [images, setImages] = useState<GeneratedImage[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [previewId, setPreviewId] = useState<string | null>(null);
  const { selectedImage, setSelectedImage } = useVisualLab();

  // Prepend newly generated images when they arrive
  React.useEffect(() => {
    if (newImages && newImages.length > 0) {
      setImages((prev) => [...newImages, ...prev]);
    }
  }, [newImages]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const deleteImage = (id: string) => {
    setImages((prev) => prev.filter((img) => img.id !== id));
    if (selectedImage?.id === id) setSelectedImage(null);
    toast.success('Bild entfernt');
  };

  const downloadImage = (img: GeneratedImage) => {
    if (!img.b64_json) {
      toast.error('Kein Bild zum Herunterladen verfügbar');
      return;
    }
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${img.b64_json}`;
    link.download = `visual-lab-${img.style.toLowerCase()}-${img.id}.png`;
    link.click();
    toast.success('Bild heruntergeladen');
  };

  const copyImageToClipboard = async (img: GeneratedImage) => {
    if (!img.b64_json) {
      toast.error('Kein Bild zum Kopieren verfügbar');
      return;
    }
    try {
      const response = await fetch(`data:image/png;base64,${img.b64_json}`);
      const blob = await response.blob();
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      toast.success('In Zwischenablage kopiert');
    } catch {
      toast.error('Kopieren nicht unterstützt');
    }
  };

  const useInEditor = (img: GeneratedImage, idx: number) => {
    const visualLabImage: VisualLabImage = {
      id: img.id,
      prompt: img.prompt,
      style: img.style,
      ratio: img.ratio,
      platform: img.platform,
      color: img.color,
      gradient: gradients[idx % gradients.length],
      seed: img.seed ?? 0,
      created: img.created,
    };
    setSelectedImage(visualLabImage);
    toast.success('Bild im Editor verknüpft', {
      description: `${img.style} · ${img.ratio} wird in der Post-Vorschau angezeigt`,
    });
  };

  const previewImage = images.find((img) => img.id === previewId);

  return (
    <div>
      {/* Gallery header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="section-label">Generierte Bilder</p>
          <h3 className="text-sm font-600 text-white mt-1">
            {generating ? (
              <span className="flex items-center gap-2">
                <Loader2 size={13} className="text-accent animate-spin" />
                <span className="text-accent mono text-xs">Bild wird generiert…</span>
              </span>
            ) : images.length === 0 ? (
              <span className="text-muted text-xs mono">Noch keine Bilder generiert</span>
            ) : (
              `${images.length} Asset${images.length !== 1 ? 's' : ''} · Brand Vault: Berger GmbH`
            )}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {selectedImage && !generating && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-accent/10 border border-accent/30 rounded text-xs mono text-accent fade-in">
              <CheckCircle size={11} />
              Im Editor aktiv: {selectedImage.style}
            </div>
          )}
          {selectedIds.size > 0 && !generating && (
            <div className="flex gap-2 fade-in">
              <button
                onClick={() => {
                  const toDownload = images.filter((img) => selectedIds.has(img.id));
                  toDownload.forEach((img) => downloadImage(img));
                }}
                className="btn-ghost text-xs gap-1.5"
              >
                <Download size={12} />
                {selectedIds.size} herunterladen
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Empty state */}
      {!generating && images.length === 0 && (
        <div className="punk-card p-12 flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 rounded-full bg-surface-2 flex items-center justify-center mb-4">
            <ImagePlus size={28} className="text-muted" />
          </div>
          <p className="text-sm font-600 text-white mb-1">Noch keine Bilder</p>
          <p className="text-xs text-muted max-w-xs">
            Gib einen Prompt ein, wähle einen Stil und klicke auf „Bild generieren" um echte KI-Bilder zu erstellen.
          </p>
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-3">
        {/* Skeleton placeholders during generation */}
        {generating && (
          <ImageSkeleton />
        )}

        {/* Generated images */}
        {!generating && images.map((img, idx) => {
          const isSelected = selectedIds.has(img.id);
          const isActiveInEditor = selectedImage?.id === img.id;
          const grad = gradients[idx % gradients.length];
          const imgSrc = img.b64_json ? `data:image/png;base64,${img.b64_json}` : null;

          return (
            <div
              key={img.id}
              className={`punk-card overflow-hidden cursor-pointer group transition-all duration-150 ${
                isActiveInEditor
                  ? 'border-accent neon-glow'
                  : isSelected
                  ? 'border-accent/60' : 'hover:border-accent/40'
              }`}
              onClick={() => toggleSelect(img.id)}
            >
              {/* Image area */}
              <div
                className={`${imgSrc ? '' : `bg-gradient-to-br ${grad}`} relative`}
                style={{
                  aspectRatio: img.ratio === '9:16' ? '9/16' : img.ratio === '1.91:1' ? '1.91' : img.ratio === '16:9' ? '16/9' : '1',
                  maxHeight: '200px',
                  overflow: 'hidden',
                }}
              >
                {imgSrc ? (
                  // Real generated image
                  <img
                    src={imgSrc}
                    alt={`Generiertes Bild: ${img.style} · ${img.ratio}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  // Fallback placeholder
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div
                        className="w-12 h-12 rounded mx-auto mb-2 opacity-60"
                        style={{ background: img.color, boxShadow: `0 0 20px ${img.color}40` }}
                      />
                      <p className="text-xs text-white/40 mono px-2 text-center leading-snug">
                        {img.style} · {img.ratio}
                      </p>
                    </div>
                  </div>
                )}

                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); setPreviewId(img.id); }}
                    className="p-2 bg-white/10 rounded hover:bg-white/20 transition-colors"
                    title="Vorschau"
                  >
                    <ExternalLink size={14} className="text-white" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); useInEditor(img, idx); }}
                    className={`p-2 rounded transition-colors ${
                      isActiveInEditor
                        ? 'bg-accent/30 hover:bg-accent/40' : 'bg-white/10 hover:bg-white/20'
                    }`}
                    title="Im Content Editor verwenden"
                  >
                    <ImagePlus size={14} className={isActiveInEditor ? 'text-accent' : 'text-white'} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); copyImageToClipboard(img); }}
                    className="p-2 bg-white/10 rounded hover:bg-white/20 transition-colors"
                    title="Kopieren"
                  >
                    <Copy size={14} className="text-white" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); downloadImage(img); }}
                    className="p-2 bg-white/10 rounded hover:bg-white/20 transition-colors"
                    title="Herunterladen"
                  >
                    <Download size={14} className="text-white" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteImage(img.id); }}
                    className="p-2 bg-red-900/40 rounded hover:bg-red-900/60 transition-colors"
                    title="Löschen"
                  >
                    <Trash2 size={14} className="text-red-400" />
                  </button>
                </div>

                {/* Active in editor badge */}
                {isActiveInEditor && (
                  <div className="absolute top-2 left-2 flex items-center gap-1 bg-accent/90 text-black text-xs mono px-1.5 py-0.5 rounded font-700">
                    <ImagePlus size={10} />
                    Editor
                  </div>
                )}

                {/* Status badge */}
                {img.status === 'fehlgeschlagen' && (
                  <div className="absolute top-2 right-2 bg-red-900/80 text-red-400 text-xs mono px-2 py-0.5 rounded">
                    Fehler
                  </div>
                )}

                {/* Selected indicator */}
                {isSelected && !isActiveInEditor && (
                  <div className="absolute top-2 left-2">
                    <CheckCircle size={18} className="text-accent" />
                  </div>
                )}
              </div>

              {/* Meta */}
              <div className="p-3">
                <p className="text-xs font-500 text-white truncate mb-1">{img.prompt}</p>
                <div className="flex items-center gap-2">
                  <span className="mono text-xs text-muted">{img.platform}</span>
                  <span className="text-muted">·</span>
                  <span className="mono text-xs text-muted">{img.ratio}</span>
                  <span className="text-muted">·</span>
                  <span className="mono text-xs" style={{ color: img.color }}>{img.style}</span>
                </div>
                <p className="mono text-xs text-muted-2 mt-1">{img.created}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Preview modal */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-8"
          onClick={() => setPreviewId(null)}
        >
          <div
            className="punk-card-accent p-6 max-w-lg w-full fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-700 text-white">Vorschau: {previewImage.style}</p>
              <button onClick={() => setPreviewId(null)} className="text-muted hover:text-white">✕</button>
            </div>
            <div className="rounded overflow-hidden mb-4" style={{ aspectRatio: '1' }}>
              {previewImage.b64_json ? (
                <img
                  src={`data:image/png;base64,${previewImage.b64_json}`}
                  alt={`Vorschau: ${previewImage.style} · ${previewImage.ratio}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className={`bg-gradient-to-br ${gradients[images.findIndex((i) => i.id === previewImage.id) % gradients.length]} w-full h-full flex items-center justify-center`}>
                  <div className="text-center">
                    <div className="w-20 h-20 rounded mx-auto mb-3" style={{ background: previewImage.color, boxShadow: `0 0 40px ${previewImage.color}60` }} />
                    <p className="text-sm text-white/60 mono">{previewImage.style} · {previewImage.ratio}</p>
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-muted">Prompt:</span>
                <span className="text-white text-right max-w-xs">{previewImage.prompt}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted">Stil:</span>
                <span className="text-white">{previewImage.style}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted">Format:</span>
                <span className="text-white">{previewImage.ratio}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted">Plattform:</span>
                <span className="text-white">{previewImage.platform}</span>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => {
                  const idx = images.findIndex((i) => i.id === previewImage.id);
                  useInEditor(previewImage, idx);
                  setPreviewId(null);
                }}
                className="btn-primary flex-1 justify-center text-xs gap-1.5"
              >
                <ImagePlus size={13} />
                Im Content Editor verwenden
              </button>
              <button
                onClick={() => downloadImage(previewImage)}
                className="btn-ghost text-xs px-4 gap-1.5"
              >
                <Download size={13} />
                Download
              </button>
              <button
                onClick={() => setPreviewId(null)}
                className="btn-ghost text-xs px-4"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}